import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const NewsletterPage = () => {
  const { getToken } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    includeLatestPosts: true
  });
  const [sending, setSending] = useState(false);
  const [sendHistory, setSendHistory] = useState([]);

  useEffect(() => {
    fetchSubscribers();
    fetchPosts();
    fetchSendHistory();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/newsletter/subscribers`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSubscribers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setSubscribers([]);
    }
  };

  const fetchPosts = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/blog/posts?status=published`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setAvailablePosts(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      setAvailablePosts([]);
    }
  };

  const fetchSendHistory = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/newsletter/history`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSendHistory(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching send history:', error);
      setSendHistory([]);
    }
  };

  const handleSendNewsletter = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send newsletter to ${subscribers.length} subscribers?`)) return;

    setSending(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://website-476522.uc.r.appspot.com';
      const response = await fetch(`${apiUrl}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...emailData,
          selectedPosts
        })
      });

      if (response.ok) {
        alert('Newsletter sent successfully!');
        setEmailData({ subject: '', message: '', includeLatestPosts: true });
        setSelectedPosts([]);
        fetchSendHistory();
      }
    } catch (error) {
      console.error('Error sending newsletter:', error);
      alert('Failed to send newsletter. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-nunito text-white">Newsletter</h1>
        <p className="mt-1 text-orange-300 font-nunito">
          Send updates to {subscribers.length} subscribers
        </p>
      </div>

      {/* Compose Newsletter */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold font-nunito text-white mb-6">Compose Newsletter</h2>
        <form onSubmit={handleSendNewsletter} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Subject Line
            </label>
            <input
              type="text"
              value={emailData.subject}
              onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
              required
              className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
              placeholder="Latest updates from Alpha Consulting..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Message
            </label>
            <textarea
              value={emailData.message}
              onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
              required
              rows="6"
              className="w-full px-4 py-2 bg-black/50 border border-white/10 rounded-lg text-white placeholder-orange-400/50 focus:outline-none focus:border-amber-500/50 font-nunito"
              placeholder="Write your newsletter message..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-orange-200 font-nunito mb-2">
              Include Blog Posts
            </label>
            <div className="space-y-2">
              {availablePosts.slice(0, 5).map((post) => (
                <label key={post.id} className="flex items-center gap-3 p-3 bg-black/30 border border-white/10 rounded-lg cursor-pointer hover:bg-white/5 transition">
                  <input
                    type="checkbox"
                    checked={selectedPosts.includes(post.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPosts([...selectedPosts, post.id]);
                      } else {
                        setSelectedPosts(selectedPosts.filter(id => id !== post.id));
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <p className="text-white font-nunito font-medium">{post.title}</p>
                    <p className="text-sm text-orange-400 font-nunito">{post.excerpt}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={sending || subscribers.length === 0}
            className="w-full py-3 px-4 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed font-nunito flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Sending...
              </>
            ) : (
              <>
                <span>📧</span>
                Send to {subscribers.length} Subscribers
              </>
            )}
          </button>
        </form>
      </div>

      {/* Send History */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold font-nunito text-white mb-6">Send History</h2>
        {sendHistory.length > 0 ? (
          <div className="space-y-3">
            {sendHistory.map((item) => (
              <div key={item.id} className="bg-black/30 border border-white/10 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-white font-nunito">{item.subject}</h3>
                    <p className="text-sm text-orange-300 font-nunito mt-1">
                      Sent to {item.recipientCount} subscribers
                    </p>
                  </div>
                  <p className="text-sm text-orange-400 font-nunito">
                    {new Date(item.sentAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-orange-400 font-nunito text-center py-8">No newsletters sent yet</p>
        )}
      </div>

      {/* Subscribers List */}
      <div className="bg-orange-900/40 border border-white/10 rounded-2xl p-6">
        <h2 className="text-xl font-bold font-nunito text-white mb-6">Subscribers ({subscribers.length})</h2>
        {subscribers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {subscribers.map((sub) => (
              <div key={sub.id} className="bg-black/30 border border-white/10 rounded-lg p-3">
                <p className="text-white font-nunito font-medium">{sub.name}</p>
                <p className="text-sm text-orange-300 font-nunito">{sub.email}</p>
                {sub.company && (
                  <p className="text-xs text-orange-400 font-nunito mt-1">{sub.company}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-orange-400 font-nunito text-center py-8">No subscribers yet</p>
        )}
      </div>
    </div>
  );
};

export default NewsletterPage;
